import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useQuery } from "@tanstack/react-query";
import { getUser, Topup } from "../services/api";
import logo from "../assets/logo2.png";
import axios from "axios";
import ServiceSelectorModal from "../components/ServiceSelectorModal";

// Icons (using emoji + text for clean look matching UI)
import {
  FaHome,
  FaHistory,
  FaCog,
  FaBell,
  FaWallet,
  FaReceipt,
  FaUndo,
  FaHourglassHalf,
  FaSignOutAlt,
  FaShoppingBag,
  FaUser,
  FaChevronRight,
  FaBars,
  FaTimes,
  FaSpinner,
  FaSync,
  FaEye,
  FaEyeSlash,
  FaCommentDots,
} from "react-icons/fa";

// ── Sidebar navigation items ──
const NAV_ITEMS = [
  { label: "Overview", icon: FaHome, id: "overview" },
  { label: "History", icon: FaHistory, id: "history" },
  { label: "Services", icon: FaShoppingBag, id: "services" },
  { label: "Notifications", icon: FaBell, id: "notifications" },
  { label: "Settings", icon: FaCog, id: "settings" },
];

export default function Dashboard() {
  const { user, logout, updateUser } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [fundModalOpen, setFundModalOpen] = useState(false);

  const { data: freshUser, refetch, isFetching } = useQuery({
    queryKey: ["profile"],
    queryFn: getUser,
    refetchInterval: 30000,
    onSuccess: (data) => updateUser?.(data),
  });

  const currentUser = freshUser || user;
  const walletBalance = parseFloat(currentUser?.wallet_balance || 0);
  const transactions = currentUser?.last_transactions || [];
  const pendingTxns = transactions.filter((t) => t.status === "pending");
  const completedTxns = transactions.filter((t) => t.status === "success");
  const name =
    `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
    currentUser?.username ||
    "Patient";
  const initials =
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good day" : "Good evening";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex">
      {/* ── Sidebar ── */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[220px] bg-white border-r border-gray-100 flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:relative md:flex`}
      >
        {/* Logo */}
        <div className="p-5 border-b border-gray-50">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center overflow-hidden shadow-sm border border-gray-100">
              <img src={logo} alt="UCH" className="w-6 h-6 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">
                UCH Self Service
              </p>
              <p className="text-gray-400 text-[10px]">
                University College Hospital
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV_ITEMS.map(({ label, icon: Icon, id }) => (
            <button
              key={id}
              onClick={() => {
                setActiveTab(id);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === id
                  ? "bg-green-50 text-[#0d3d2e] font-semibold"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <Icon
                className={`w-4 h-4 ${activeTab === id ? "text-[#0d3d2e]" : "text-gray-400"}`}
              />
              {label}
            </button>
          ))}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={() => navigate("/support")}
            className="w-full flex items-center gap-2 px-3 py-2 mb-1 text-xs text-gray-400 hover:text-[#0d3d2e] hover:bg-green-50 rounded-xl transition"
          >
            <FaUser className="w-3 h-3" /> Support
          </button>
          <button
            onClick={() => navigate("/feedback")}
            className="w-full flex items-center gap-2 px-3 py-2 mb-1 text-xs text-gray-400 hover:text-[#0d3d2e] hover:bg-green-50 rounded-xl transition"
          >
            <FaCommentDots className="w-3 h-3" /> Feedback
          </button>
          <div
            className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 cursor-pointer"
            onClick={() => navigate("/profile/edit")}
          >
            <div className="w-9 h-9 bg-[#0d3d2e] rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {name}
              </p>
              <p className="text-xs text-gray-400">Patient</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 mt-1 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
          >
            <FaSignOutAlt className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <FaBars className="w-4 h-4 text-gray-600" />
            </button>
            <div className="hidden md:block">
              <p className="text-xs text-gray-400">{greeting},</p>
              <p className="font-bold text-gray-900 text-lg leading-tight">
                {name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Cart count badge */}
            {cart.length > 0 && (
              <button
                onClick={() => navigate("/wallet-pay")}
                className="relative p-2 text-gray-500 hover:text-[#0d3d2e] transition"
              >
                <FaShoppingBag className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cart.reduce((s, i) => s + i.quantity, 0)}
                </span>
              </button>
            )}
            <button
              onClick={() => setServiceModalOpen(true)}
              className="bg-[#0d3d2e] hover:bg-[#0a3226] text-white text-sm font-semibold px-4 py-2 rounded-xl transition flex items-center gap-2"
            >
              <FaShoppingBag className="w-3.5 h-3.5" /> Pay for services
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto">
          {activeTab === "overview" && (
            <OverviewContent
              user={currentUser}
              walletBalance={walletBalance}
              transactions={transactions}
              pendingTxns={pendingTxns}
              completedTxns={completedTxns}
              cart={cart}
              navigate={navigate}
              setActiveTab={setActiveTab}
              refetch={refetch}
              isFetching={isFetching}
              onFundClick={() => setFundModalOpen(true)}
              onServiceClick={() => setServiceModalOpen(true)}
            />
          )}
          {activeTab === "history" && (
            <HistoryContent
              transactions={transactions}
              navigate={navigate}
              refetch={refetch}
              user={currentUser}
              label="Transaction History"
              showAll
            />
          )}
          {activeTab === "receipts" && (
            <HistoryContent
              transactions={transactions.filter(
                (t) => t.transaction_type === "debit",
              )}
              navigate={navigate}
              refetch={refetch}
              user={currentUser}
              label="Service Receipts"
            />
          )}
          {activeTab === "services" && (
            <ServicesContent onOpen={() => setServiceModalOpen(true)} />
          )}
          {activeTab === "notifications" && <NotificationsContent />}
          {activeTab === "settings" && <SettingsContent navigate={navigate} />}
        </main>
      </div>

      {/* ── Service Selector Modal ── */}
      <ServiceSelectorModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        redirectTo="/wallet-pay"
      />

      {/* ── Fund Wallet Modal ── */}
      <FundWalletInlineModal
        open={fundModalOpen}
        onClose={() => setFundModalOpen(false)}
        walletBalance={walletBalance}
        refetch={refetch}
      />
    </div>
  );
}

// ── Inline Fund Wallet Modal ──
function FundWalletInlineModal({ open, onClose, walletBalance, refetch }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [fundMethod, setFundMethod] = useState("monnify");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const SERVICE_CHARGE = 100;
  const quickAmounts = [1000, 2000, 5000, 10000, 20000, 50000];

  const loadScript = (src) =>
    new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = src;
      s.onload = res;
      s.onerror = rej;
      document.body.appendChild(s);
    });

  const handleFund = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt < 100) {
      setStatus({ type: "error", msg: "Minimum deposit is ₦100" });
      return;
    }
    setLoading(true);
    setStatus(null);

    if (fundMethod === "monnify") {
      try {
        const res = await Topup({ amount: amt, payment_method: "monnify" });
        const reference = res.txn_reference || res.reference;
        const grossAmount = parseFloat(res.rrr_response?.gross_amount || res.gross_amount || amt + 100);
        if (!window.MonnifySDK) {
          await loadScript("https://sdk.monnify.com/plugin/monnify.js");
          await new Promise(r => setTimeout(r, 500));
        }
        const monnifyRef = `${reference}-${Date.now()}`;
        window.MonnifySDK.initialize({
          amount: grossAmount,
          currency: "NGN",
          reference: monnifyRef,
          customerFullName: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email,
          customerEmail: user?.email,
          apiKey: "MK_PROD_4XA24J4TGS",
          contractCode: "765974016458",
          paymentDescription: "UCH Wallet Top-up",
          paymentMethods: ["CARD", "ACCOUNT_TRANSFER"],
          metadata: {},
          onLoadStart: function() {},
          onLoadComplete: function() {},
          onComplete: function(data) {
            if (data.status === "SUCCESS") {
              axios.get(
                `https://api.uchselfservice.net/api/express/callback/top-up/monnify/?transaction_id=${reference}&monnify_ref=${data.paymentReference || monnifyRef}&amount=${grossAmount}`
              ).then(function() {
                refetch?.();
                setStatus({ type: "success", msg: `₦${amt.toLocaleString()} successfully added to your wallet!` });
                setTimeout(() => onClose(), 2000);
              }).catch(function() {
                setStatus({ type: "success", msg: "Payment done. Balance will update shortly." });
              });
            } else if (data.status === "FAILED") {
              setStatus({ type: "error", msg: "Payment failed. Please try again." });
            } else {
              setStatus({ type: "info", msg: "Payment window closed." });
            }
          },
          onClose: function() { setStatus({ type: "info", msg: "Payment window closed." }); },
        });
      } catch {
        setStatus({ type: "error", msg: "Failed to initialize Monnify payment." });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Remita flow
    try {
      if (!window.RmPaymentEngine) {
        await loadScript("https://login.remita.net/payment/v1/remita-pay-inline.bundle.js");
      }
      const res = await Topup({ amount: amt, payment_method: "remita" });
      const grossAmount = parseFloat(res.rrr_response?.gross_amount || res.gross_amount || amt + 100);
      window.RmPaymentEngine.init({
        key: "QVZQTHw2MzU4MjA1MDYzfDFkN2UxYTgxNmUwMzQzMzhkMWM2ZmYzMTRiYzgxYmFlNDNkNzVlZTliNDI0ZWZhODQ0YWU5MWJkY2I1OTYwZTMyYWFkYzljNWMwZDIxMjcxOTA1NDI0YjA5OWI1MTlkNTM4MTk0MGI0YjMzMmE2M2E3NmFjY2ZhNzQyYjE0Y2Y1",
        transactionId: res.reference,
        processRrr: true,
        customerId: user?.email,
        firstName: user?.first_name || "",
        lastName: user?.last_name || "",
        email: user?.email,
        amount: grossAmount,
        narration: "Wallet top-up",
        extendedData: { customFields: [{ name: "rrr", value: res.rrr_response?.RRR }] },
        onSuccess: async (data) => {
          await axios.get(
            `https://api.uchselfservice.net/api/express/callback/top-up/remita/?transaction_id=${data.transactionId}&rrr=${res.rrr_response?.RRR}&amount=${grossAmount}`,
          );
          await refetch?.();
          setStatus({ type: "success", msg: `₦${amt.toLocaleString()} successfully added to your wallet!` });
          setTimeout(() => onClose(), 2000);
        },
        onError: () => setStatus({ type: "error", msg: "Payment failed. Please try again." }),
        onClose: () => setStatus({ type: "info", msg: "Payment window closed." }),
      }).showPaymentWidget();
    } catch {
      setStatus({ type: "error", msg: "Failed to initialize payment." });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] px-6 py-5 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">Fund Wallet</h3>
            <p className="text-green-300 text-xs mt-0.5">
              Balance: ₦
              {walletBalance.toLocaleString("en-NG", {
                minimumFractionDigits: 2,
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition"
          >
            <FaTimes className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Quick amounts
            </label>
            <div className="grid grid-cols-3 gap-2">
              {quickAmounts.map((a) => (
                <button
                  key={a}
                  onClick={() => setAmount(String(a))}
                  className={`py-2.5 rounded-xl text-sm font-semibold transition border-2 ${amount === String(a) ? "border-[#0d3d2e] bg-green-50 text-[#0d3d2e]" : "border-gray-100 bg-gray-50 text-gray-700 hover:border-green-300"}`}
                >
                  ₦{a.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Or enter amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">
                ₦
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setStatus(null);
                }}
                placeholder="Enter amount"
                min="100"
                className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            {amount && parseInt(amount) >= 100 && (
              <div className="mt-2 text-xs text-gray-500 flex justify-between bg-gray-50 rounded-lg px-3 py-2">
                <span>
                  Deposit:{" "}
                  <strong>₦{parseInt(amount || 0).toLocaleString()}</strong>
                </span>
                <span>
                  Service charge: <strong>₦{SERVICE_CHARGE}</strong>
                </span>
                <span>
                  Total debit:{" "}
                  <strong>
                    ₦{(parseInt(amount || 0) + SERVICE_CHARGE).toLocaleString()}
                  </strong>
                </span>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Payment Method
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "monnify", label: "Card / Transfer", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQk4cKO96nyvsKiIGEnENpe37jAz5lTgBr5-g&s" },
                { key: "remita", label: "Remita", logo: "https://blog.remita.net/wp-content/uploads/2024/04/ROUNDLOGO-1920x1024.png" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFundMethod(opt.key)}
                  className={`flex flex-col items-center p-2.5 rounded-xl border-2 transition ${
                    fundMethod === opt.key
                      ? "border-[#0d3d2e] bg-green-50"
                      : "border-gray-100 hover:border-green-300"
                  }`}
                >
                  <img src={opt.logo} alt={opt.label} className="h-6 w-full object-contain mb-1" />
                  <span className="text-xs font-medium text-gray-700">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
          {status && (
            <div
              className={`px-4 py-3 rounded-xl text-sm font-medium ${
                status.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : status.type === "error"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-blue-50 text-blue-700 border border-blue-100"
              }`}
            >
              {status.msg}
            </div>
          )}
          <button
            onClick={handleFund}
            disabled={loading || !amount || parseInt(amount) < 100}
            className="w-full bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold py-3.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" /> Processing...
              </>
            ) : (
              `Pay with ${fundMethod === "monnify" ? "Card / Transfer" : "Remita"}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Overview ──
function OverviewContent({
  user,
  walletBalance,
  transactions,
  pendingTxns,
  completedTxns,
  cart,
  navigate,
  setActiveTab,
  refetch,
  isFetching,
  onFundClick,
  onServiceClick,
}) {
  const name =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Patient";
  const totalDeposited = transactions
    .filter((t) => t.transaction_type === "credit" && t.status === "success")
    .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
  const cartTotal = (cart || []).reduce(
    (s, i) => s + parseFloat(i.price || 0) * (i.quantity || 1),
    0,
  );
  const [balanceVisible, setBalanceVisible] = useState(
    () => localStorage.getItem("wallet_balance_hidden") !== "true",
  );
  const toggleBalanceVisible = () => {
    setBalanceVisible((prev) => {
      const next = !prev;
      localStorage.setItem("wallet_balance_hidden", String(!next));
      return next;
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Wallet card */}
      <div className="bg-gradient-to-br from-[#0d3d2e] via-[#0f4a38] to-[#0a3226] rounded-2xl p-7 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-green-300 text-xs font-semibold uppercase tracking-widest">
              WALLET BALANCE
            </p>
            <button
              onClick={() => refetch?.()}
              title="Refresh balance"
              className="text-green-300 hover:text-white transition p-0.5"
            >
              <FaSync className={`w-3 h-3 ${isFetching ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={toggleBalanceVisible}
              title={balanceVisible ? "Hide balance" : "Show balance"}
              className="text-green-300 hover:text-white transition p-0.5"
            >
              {balanceVisible ? (
                <FaEye className="w-3 h-3" />
              ) : (
                <FaEyeSlash className="w-3 h-3" />
              )}
            </button>
          </div>
          <p className="text-5xl font-black mb-5">
            {balanceVisible
              ? `₦${walletBalance.toLocaleString("en-NG", {
                  minimumFractionDigits: 2,
                })}`
              : "₦••••••"}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onFundClick}
              className="bg-white text-[#0d3d2e] font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-green-50 transition"
            >
              Fund wallet
            </button>
            <button
              onClick={onServiceClick}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold text-sm px-5 py-2.5 rounded-xl transition"
            >
              Pay for services
            </button>
          </div>
        </div>
      </div>

      {/* Stats — compact row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
          {
            label: "Total Deposited",
            value: `₦${totalDeposited.toLocaleString()}`,
            sub: "All time",
            dot: "bg-green-500",
          },
          {
            label: "Pending Deposits",
            value: pendingTxns.length,
            sub: "Awaiting confirmation",
            dot: "bg-yellow-400",
          },
          {
            label: "Successful Payments",
            value: completedTxns.length,
            sub: "Completed",
            dot: "bg-green-500",
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-2xl p-4 border border-gray-100"
          >
            <p className="text-xs text-gray-400 font-medium mb-2">{s.label}</p>
            <p className="text-xl font-black text-gray-900 mb-1">{s.value}</p>
            <p className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {s.sub}
            </p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[
          {
            icon: FaUndo,
            label: "Refund",
            action: () => navigate("/withdraw"),
          },
          {
            icon: FaHourglassHalf,
            label: `Pending (${pendingTxns.length})`,
            action: () => setActiveTab("history"),
          },
          {
            icon: FaHistory,
            label: "History",
            action: () => setActiveTab("history"),
          },
          { icon: FaWallet, label: "Fund wallet", action: onFundClick },
          {
            icon: FaReceipt,
            label: "Receipts",
            action: () => setActiveTab("receipts"),
          },
          {
            icon: FaUser,
            label: "My account",
            action: () => navigate("/profile/edit"),
          },
        ].map(({ icon: Icon, label, action }) => (
          <button
            key={label}
            onClick={action}
            className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 hover:shadow-md hover:border-green-100 transition group"
          >
            <div className="w-11 h-11 bg-[#f5f5f0] group-hover:bg-green-50 rounded-xl flex items-center justify-center transition">
              <Icon className="w-5 h-5 text-[#0d3d2e]" />
            </div>
            <p className="text-xs font-medium text-gray-700 text-center leading-tight">
              {label}
            </p>
          </button>
        ))}
      </div>

      {/* Bottom section */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent transactions */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-50">
            <h3 className="font-bold text-gray-900">Recent transactions</h3>
            <button
              onClick={() => setActiveTab("history")}
              className="text-xs text-green-700 font-semibold hover:underline flex items-center gap-1"
            >
              See all <FaChevronRight className="w-2.5 h-2.5" />
            </button>
          </div>
          <div>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">
                No transactions yet
              </div>
            ) : (
              transactions
                .slice(0, 5)
                .map((tx) => (
                  <TxRow
                    key={tx.id}
                    tx={tx}
                    navigate={navigate}
                    refetch={refetch}
                    user={user}
                  />
                ))
            )}
          </div>
        </div>

        {/* Account summary */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-bold text-gray-900 mb-5">Account summary</h3>
          <div className="space-y-4">
            {[
              [
                "Patient",
                `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
                  "—",
              ],
              [
                "Balance",
                `₦${walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`,
              ],
              ["Pending Deposit", pendingTxns.length],
              [
                "Bills due",
                `₦${cartTotal.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`,
              ],
              ["Txn fee", "₦100 (deposits)"],
              [
                "Last activity",
                transactions[0]
                  ? new Date(transactions[0].created_at).toLocaleString(
                      "en-GB",
                      { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" },
                    )
                  : "—",
              ],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0"
              >
                <span className="text-xs text-gray-400">{k}</span>
                <span className="text-sm font-semibold text-gray-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Transaction Row ──
function TxRow({ tx, navigate, refetch, user }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const isDeposit = tx.transaction_type === "credit";
  const isSuccess = tx.status === "success";
  const isPending = tx.status === "pending";
  const amount = parseFloat(tx.gross_amount || tx.amount || 0);

  const openRemitaWidget = async (rrr) => {
    /** Re-open Remita payment widget with the existing RRR so user can complete payment */
    try {
      if (!window.RmPaymentEngine || typeof window.RmPaymentEngine.init !== "function") {
        const s = document.createElement("script");
        s.src = "https://login.remita.net/payment/v1/remita-pay-inline.bundle.js";
        s.async = true;
        await new Promise((res, rej) => { s.onload = res; s.onerror = rej; document.body.appendChild(s); });
        await new Promise(r => setTimeout(r, 500));
      }
      if (!window.RmPaymentEngine) throw new Error("Remita failed to load");

      // Remita's SDK rejects re-initializing a widget session with a transactionId
      // it has already seen — generate a fresh one per retry, and tie it back to
      // the original pending transaction via the RRR (passed in extendedData).
      const retryTransactionId = `${tx.reference || "requery"}-r${Date.now()}`;

      window.RmPaymentEngine.init({
        key: "QVZQTHw2MzU4MjA1MDYzfDFkN2UxYTgxNmUwMzQzMzhkMWM2ZmYzMTRiYzgxYmFlNDNkNzVlZTliNDI0ZWZhODQ0YWU5MWJkY2I1OTYwZTMyYWFkYzljNWMwZDIxMjcxOTA1NDI0YjA5OWI1MTlkNTM4MTk0MGI0YjMzMmE2M2E3NmFjY2ZhNzQyYjE0Y2Y1",
        processRrr: true,
        transactionId: retryTransactionId,
        customerId: user?.email || "",
        firstName: user?.first_name || "",
        lastName: user?.last_name || "",
        email: user?.email || "",
        amount,
        narration: `Wallet top-up — ${tx.reference || "requery"}`,
        extendedData: { customFields: [{ name: "rrr", value: rrr }] },
        onSuccess: async (data) => {
          setLoading(true);
          try {
            await axios.get(
              `https://api.uchselfservice.net/api/express/callback/top-up/remita/?transaction_id=${tx.reference || data.transactionId}&rrr=${rrr}&amount=${amount}`
            );
            setMsg({ type: "ok", text: "✓ Payment complete — your wallet has been credited!" });
            setTimeout(() => { refetch?.(); }, 1200);
          } catch {
            setMsg({ type: "warn", text: "Payment done but verification pending. Check your balance shortly." });
          } finally {
            setLoading(false);
          }
        },
        onError: () => setMsg({ type: "err", text: "Payment failed. Please try again." }),
        onClose: () => setMsg({ type: "warn", text: "Payment window closed. Click Requery to try again." }),
      }).showPaymentWidget();
    } catch (e) {
      setMsg({ type: "err", text: "Could not open payment window. Check your connection." });
    }
  };

  const handleProceed = async () => {
    if (loading) return;
    setLoading(true);
    setMsg(null);
    const rrr = tx.rrr || "";
    const ref = tx.reference || "";
    try {
      // First: check if Remita has already confirmed this payment
      const params = new URLSearchParams({ rrr, amount });
      if (ref) params.set("transaction_id", ref);
      const res = await axios.get(
        `https://api.uchselfservice.net/api/express/callback/top-up/remita/?${params.toString()}`
      );

      if (res.data?.status === "success" || res.data?.transaction_status === "SUCCESS") {
        // Remita confirmed — wallet credited
        setMsg({ type: "ok", text: "✓ Payment confirmed — your wallet has been credited!" });
        setTimeout(() => { refetch?.(); }, 1200);
      } else if (res.data?.paid_at === null || res.data?.status === "pending") {
        // Not yet paid — re-open the Remita widget with the same RRR
        setLoading(false);
        setMsg({ type: "info", text: "Opening Remita payment to complete your transaction..." });
        await openRemitaWidget(rrr);
      } else {
        setMsg({ type: "warn", text: "Payment not yet confirmed. Try again shortly or contact support." });
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.message || "Could not reach Remita.";
      setMsg({ type: "err", text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 py-4 flex items-center justify-between border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition">
      <div className="flex items-center gap-3.5 min-w-0">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold ${isDeposit ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}
        >
          {isDeposit ? "+" : "−"}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {isDeposit ? "Wallet top-up" : tx.description || "Service payment"}
          </p>
          <p className="text-xs text-gray-400">
            {tx.created_at
              ? new Date(tx.created_at).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—"}
            &nbsp;·&nbsp;
            {isDeposit ? "Deposit" : "Payment"}
          </p>
          {msg && (
            <p
              className={`text-xs mt-0.5 font-medium ${msg.type === "ok" ? "text-green-600" : msg.type === "warn" ? "text-yellow-600" : "text-red-500"}`}
            >
              {msg.text}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2.5 shrink-0">
        <span
          className={`text-sm font-bold ${isDeposit ? "text-green-700" : "text-gray-900"}`}
        >
          {isDeposit ? "+" : "−"}₦
          {amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
          isSuccess ? "bg-green-100 text-green-700"
          : (isPending && isDeposit && !tx.rrr) ? "bg-red-50 text-red-600"
          : isPending ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
          : "bg-red-50 text-red-600"
        }`}>
          {isPending && isDeposit && !tx.rrr ? "Failed"
           : isPending && isDeposit ? "Pending Deposit"
           : tx.status?.charAt(0).toUpperCase() + tx.status?.slice(1)}
        </span>
        {/* Requery only when there IS a Remita RRR */}
        {isPending && isDeposit && tx.rrr && (
          <button
            onClick={handleProceed}
            disabled={loading}
            className="text-xs bg-[#0d3d2e] hover:bg-[#0a3226] text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-60"
          >
            {loading ? <FaSpinner className="animate-spin w-3 h-3" /> : <FaSync className="w-3 h-3" />}
            {loading ? "Checking..." : "Requery"}
          </button>
        )}
        <button
          onClick={() =>
            navigate("/wallet/transaction-receipt", {
              state: {
                transaction: {
                  ...tx,
                  type: isDeposit ? "deposit" : "charge",
                  amount: tx.amount,
                  date: tx.created_at
                    ? new Date(tx.created_at).toLocaleDateString("en-GB")
                    : "—",
                },
              },
            })
          }
          className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition"
        >
          {isPending ? "Invoice" : "Receipt"}
        </button>
      </div>
    </div>
  );
}

// ── History Tab ──
function HistoryContent({
  transactions,
  navigate,
  refetch,
  user,
  label = "Transaction History",
  showAll = false,
}) {
  const [search, setSearch] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [statusF, setStatusF] = useState("all");

  const filtered = transactions.filter((tx) => {
    if (typeF !== "all" && tx.transaction_type !== typeF) return false;
    if (statusF !== "all" && tx.status !== statusF) return false;
    if (
      search &&
      !JSON.stringify(tx).toLowerCase().includes(search.toLowerCase())
    )
      return false;
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-black text-gray-900 mb-6">{label}</h2>
      <div className="bg-white rounded-2xl border border-gray-100 mb-5 p-4 flex flex-wrap gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search transactions..."
          className="flex-1 min-w-[180px] px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <select
          value={typeF}
          onChange={(e) => setTypeF(e.target.value)}
          className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none"
        >
          <option value="all">All types</option>
          <option value="credit">Deposits</option>
          <option value="debit">Payments</option>
        </select>
        <select
          value={statusF}
          onChange={(e) => setStatusF(e.target.value)}
          className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none"
        >
          <option value="all">All status</option>
          <option value="success">Success</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            No transactions match.
          </div>
        ) : (
          filtered.map((tx) => (
            <TxRow key={tx.id} tx={tx} navigate={navigate} refetch={refetch} user={user} />
          ))
        )}
      </div>
    </div>
  );
}

function ServicesContent({ onOpen }) {
  // Immediately show the service selector
  React.useEffect(() => {
    onOpen();
  }, []);
  return (
    <div className="max-w-2xl mx-auto text-center py-16 space-y-4">
      <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto">
        <FaShoppingBag className="text-[#0d3d2e] w-8 h-8" />
      </div>
      <h2 className="text-xl font-black text-gray-900">Select a Service</h2>
      <p className="text-gray-500 text-sm">
        Browse and select UCH hospital services to pay with your wallet.
      </p>
      <button
        onClick={onOpen}
        className="bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold px-6 py-3 rounded-xl transition text-sm inline-flex items-center gap-2"
      >
        <FaShoppingBag className="w-4 h-4" /> Browse Services
      </button>
    </div>
  );
}

function NotificationsContent() {
  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-black text-gray-900 mb-6">Notifications</h2>
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <FaBell className="w-10 h-10 text-gray-200 mx-auto mb-4" />
        <p className="text-gray-400 text-sm">No new notifications</p>
      </div>
    </div>
  );
}

function SettingsContent({ navigate }) {
  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-black text-gray-900 mb-6">Settings</h2>
      <div className="space-y-3">
        {[
          {
            label: "Edit Profile",
            desc: "Update your name, email and hospital number",
            path: "/profile/edit",
          },
          {
            label: "Change Password",
            desc: "Update your account password",
            path: "/profile/edit",
          },
          {
            label: "Request Refund",
            desc: "Submit a wallet refund request",
            path: "/withdraw",
          },
        ].map((s) => (
          <button
            key={s.label}
            onClick={() => navigate(s.path)}
            className="w-full bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between hover:shadow-md hover:border-green-100 transition text-left group"
          >
            <div>
              <p className="font-semibold text-gray-900 text-sm">{s.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>
            </div>
            <FaChevronRight className="text-gray-300 group-hover:text-green-600 transition w-4 h-4" />
          </button>
        ))}
      </div>
    </div>
  );
}
